import { type MutableRefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { bind, type UnbindFn } from 'bind-event-listener';

import { useHoverDelay } from './useHoverDelay';
import { getChildPortalFromParentPopup, hasRelatedTarget, isMovingToKudos } from './utils';

const HOVER_OPEN_DELAY = 800;
const HOVER_CLOSE_DELAY = 200;

export type UseHoverTriggerRefProps = {
	onOpen: () => void;
	onClose?: () => void;
	isOpen?: boolean;
	isDisabled?: boolean;
	openedByRef?: MutableRefObject<'hover' | 'click' | null>;
	delays?: {
		hoverOpen?: number;
		hoverClose?: number;
	};
};

/**
 * Provides hover behavior for a popup trigger and its content with delayed open/close.
 *
 * This hook manages mouse event listeners for both the trigger and popup content,
 * supporting delayed open/close to prevent accidental activation. It also tracks
 * child portals (such as dropdown menus rendered in a portal) to keep the popup
 * open when hovering over them. All event logic is managed via refs to avoid stale closures.
 */
export function useHoverTriggerRef({
	onOpen,
	onClose,
	isOpen = false,
	isDisabled = false,
	openedByRef,
	delays = {},
}: UseHoverTriggerRefProps): {
	triggerRef: (element: HTMLElement | null) => void;
	contentRef: (element: HTMLElement | null) => void;
} {
	const { hoverOpen = HOVER_OPEN_DELAY, hoverClose = HOVER_CLOSE_DELAY } = delays;

	const triggerNodeRef = useRef<HTMLElement | null>(null);
	const triggerListeners = useRef<UnbindFn[]>([]);
	const contentNodeRef = useRef<HTMLElement | null>(null);
	const contentListeners = useRef<Map<Element, UnbindFn>>(new Map());
	const isOpenRef = useRef(isOpen);

	useLayoutEffect(() => {
		isOpenRef.current = isOpen;
	}, [isOpen]);

	const openDelay = useHoverDelay(() => {
		if (isOpenRef.current) {
			return;
		}
		if (openedByRef) {
			openedByRef.current = 'hover';
		}
		onOpen();
	}, hoverOpen);

	const closeDelay = useHoverDelay(() => {
		if (!isOpenRef.current) {
			return;
		}
		if (!openedByRef || openedByRef.current === 'hover') {
			onClose?.();
		}
	}, hoverClose);

	const openDelayRef = useRef(openDelay);
	const closeDelayRef = useRef(closeDelay);
	openDelayRef.current = openDelay;
	closeDelayRef.current = closeDelay;

	const handleTriggerEnter = useCallback(() => {
		closeDelayRef.current.clear();
		openDelayRef.current.schedule();
	}, []);

	const handleTriggerLeave = useCallback(() => {
		openDelayRef.current.clear();
		closeDelayRef.current.schedule();
	}, []);

	const triggerRef = useCallback(
		(element: HTMLElement | null): void => {
			if (element === triggerNodeRef.current) {
				return;
			}

			triggerListeners.current.forEach((unbind) => unbind());
			triggerListeners.current = [];
			triggerNodeRef.current = element;

			if (element && !isDisabled) {
				triggerListeners.current.push(
					bind(element, { type: 'mouseenter', listener: handleTriggerEnter }),
					bind(element, { type: 'mouseleave', listener: handleTriggerLeave }),
				);
			}
		},
		[handleTriggerEnter, handleTriggerLeave, isDisabled],
	);

	const handleContentEnter = useCallback(() => {
		closeDelayRef.current.clear();
	}, []);

	const cleanupContentListeners = useCallback(() => {
		contentListeners.current.forEach((unbind) => unbind());
		contentListeners.current.clear();
	}, []);

	const attachContentListeners = useCallback(
		(element: Element) => {
			if (contentListeners.current.has(element)) {
				return;
			}

			const handleMouseLeave = (event: Event) => {
				if (!hasRelatedTarget(event)) {
					return;
				}

				const relatedTarget = event.relatedTarget;

				if (isMovingToKudos(relatedTarget)) {
					return;
				}

				const childPortal = getChildPortalFromParentPopup(relatedTarget, contentNodeRef.current);
				if (childPortal) {
					attachContentListeners(childPortal);
					return;
				}

				closeDelayRef.current.schedule();
			};

			const unbindEnter = bind(element, { type: 'mouseenter', listener: handleContentEnter });
			const unbindLeave = bind(element, { type: 'mouseleave', listener: handleMouseLeave });

			contentListeners.current.set(element, () => {
				unbindEnter();
				unbindLeave();
			});
		},
		[handleContentEnter],
	);

	const contentRef = useCallback(
		(element: HTMLElement | null): void => {
			if (element === contentNodeRef.current) {
				return;
			}

			cleanupContentListeners();
			contentNodeRef.current = element;

			if (element && !isDisabled) {
				attachContentListeners(element);
			}
		},
		[attachContentListeners, cleanupContentListeners, isDisabled],
	);

	useEffect(() => {
		const listeners = contentListeners.current;
		const triggers = triggerListeners.current;
		return () => {
			openDelayRef.current.clear();
			closeDelayRef.current.clear();
			triggers.forEach((unbind) => unbind());
			listeners.forEach((unbind) => unbind());
		};
	}, []);

	return { triggerRef, contentRef };
}
